import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { 
  BarChart3, MousePointer, Eye, FileText, Download, RefreshCw, 
  LogOut, TrendingUp, Clock, Globe, ArrowRight
} from 'lucide-react';

const AdminAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check if admin is logged in
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Fetch analytics
  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics`);
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/login');
  };

  // Download CSV
  const downloadCSV = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export`);
      const data = await response.json();
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pricestrike-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">
                Website Analytics
              </h1>
              <p className="text-[#94A3B8]">
                Track page views, CTA clicks, and user interactions
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/admin/subscribers">
                <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10">
                  View Subscribers
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-[#2563EB]/30 border-t-[#2563EB] rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#94A3B8]">Loading analytics...</p>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
              <p className="text-red-400">{error}</p>
              <Button onClick={fetchAnalytics} className="mt-4">Try Again</Button>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#2563EB]/10 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-[#F8FAFC]">{analytics?.total_events || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Total Events</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-[#F8FAFC]">{analytics?.today_events || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Today</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                      <Eye className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-[#F8FAFC]">{analytics?.events_by_type?.page_view || 0}</p>
                      <p className="text-sm text-[#94A3B8]">Page Views</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                      <MousePointer className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-[#F8FAFC]">{analytics?.events_by_type?.cta_click || 0}</p>
                      <p className="text-sm text-[#94A3B8]">CTA Clicks</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions Bar */}
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-4 mb-6">
                <div className="flex gap-3 justify-end">
                  <Button
                    onClick={fetchAnalytics}
                    variant="outline"
                    className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                  <Button
                    onClick={downloadCSV}
                    className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Page Views */}
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="w-5 h-5 text-[#2563EB]" />
                    <h2 className="text-lg font-semibold text-[#F8FAFC]">Page Views</h2>
                  </div>
                  {analytics?.page_views && Object.keys(analytics.page_views).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(analytics.page_views)
                        .sort(([,a], [,b]) => b - a)
                        .map(([page, count]) => (
                          <div key={page} className="flex items-center justify-between">
                            <span className="text-[#94A3B8] text-sm truncate max-w-[200px]">{page}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-[#0A0F1C] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-[#2563EB] rounded-full"
                                  style={{ width: `${Math.min((count / Math.max(...Object.values(analytics.page_views))) * 100, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-[#F8FAFC] font-medium w-12 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-[#94A3B8] text-sm">No page views recorded yet</p>
                  )}
                </div>

                {/* CTA Clicks */}
                <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MousePointer className="w-5 h-5 text-orange-400" />
                    <h2 className="text-lg font-semibold text-[#F8FAFC]">CTA Clicks</h2>
                  </div>
                  {analytics?.cta_clicks && Object.keys(analytics.cta_clicks).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(analytics.cta_clicks)
                        .sort(([,a], [,b]) => b - a)
                        .map(([element, count]) => (
                          <div key={element} className="flex items-center justify-between">
                            <span className="text-[#94A3B8] text-sm truncate max-w-[200px]">{element}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-[#0A0F1C] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-orange-500 rounded-full"
                                  style={{ width: `${Math.min((count / Math.max(...Object.values(analytics.cta_clicks))) * 100, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-[#F8FAFC] font-medium w-12 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-[#94A3B8] text-sm">No CTA clicks recorded yet</p>
                  )}
                </div>
              </div>

              {/* Recent Events */}
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-[#2563EB]/20 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#2563EB]" />
                  <h2 className="text-lg font-semibold text-[#F8FAFC]">Recent Events</h2>
                </div>
                {analytics?.recent_events && analytics.recent_events.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#2563EB]/20">
                          <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Time</th>
                          <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Type</th>
                          <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Page</th>
                          <th className="text-left text-sm font-medium text-[#94A3B8] p-4">Element</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recent_events.slice(0, 20).map((event, index) => (
                          <tr 
                            key={event.id || index} 
                            className="border-b border-[#2563EB]/10 hover:bg-[#2563EB]/5 transition-colors"
                          >
                            <td className="p-4 text-[#94A3B8] text-sm">
                              {formatTime(event.timestamp)}
                            </td>
                            <td className="p-4">
                              <span className={`text-xs px-2 py-1 rounded ${
                                event.event_type === 'page_view' ? 'bg-purple-500/10 text-purple-400' :
                                event.event_type === 'cta_click' ? 'bg-orange-500/10 text-orange-400' :
                                event.event_type === 'form_submit' ? 'bg-green-500/10 text-green-400' :
                                'bg-[#2563EB]/10 text-[#2563EB]'
                              }`}>
                                {event.event_type}
                              </span>
                            </td>
                            <td className="p-4 text-[#F8FAFC] text-sm">{event.page}</td>
                            <td className="p-4 text-[#94A3B8] text-sm">{event.element || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <BarChart3 className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                    <p className="text-[#F8FAFC] font-semibold mb-2">No events recorded yet</p>
                    <p className="text-[#94A3B8] text-sm">Events will appear here as users interact with the site</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminAnalyticsPage;
